/**
 * WaveText — animates each character with a scale + glow wave effect.
 * The wave propagates across the full text in exactly `waveDurationMs`,
 * regardless of text length. The animation triggers every `intervalSec` seconds.
 *
 * @param text           - string to display
 * @param intervalSec    - seconds between each auto-trigger (default 29)
 * @param waveDurationMs - duration of the full wave pass across all characters (default 1000)
 * @param charDurationMs - duration of the animation on each individual character (default 600)
 * @param firstDelayMs   - delay in ms before the very first wave triggers after mount (default 0)
 * @param className      - optional extra class on the root element
 */

import { useEffect, useRef, useState } from 'react';
import styles from './WaveText.module.css';

interface WaveTextProps {
    text: string;
    intervalSec?: number;
    waveDurationMs?: number;
    charDurationMs?: number;
    /** Delay before the very first wave after mount. Subsequent waves use intervalSec. */
    firstDelayMs?: number;
    className?: string;
}

export default function WaveText({
    text,
    intervalSec = 29,
    waveDurationMs = 1000,
    charDurationMs = 600,
    firstDelayMs = 0,
    className,
}: WaveTextProps) {
    const [isAnimating, setIsAnimating] = useState(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const letters = [...text];
    // Delay between each letter so the wave spans exactly waveDurationMs
    const delayPerLetterMs = letters.length > 1 ? waveDurationMs / (letters.length - 1) : 0;

    const triggerWave = () => {
        setIsAnimating(true);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        // Total duration: wave pass + last character's own animation
        const totalMs = waveDurationMs + charDurationMs;
        timeoutRef.current = setTimeout(() => setIsAnimating(false), totalMs);
    };

    useEffect(() => {
        // First wave fires after firstDelayMs, then repeats every intervalSec
        const firstTimeout = setTimeout(() => {
            triggerWave();
            intervalRef.current = setInterval(triggerWave, intervalSec * 1000);
        }, firstDelayMs);

        return () => {
            clearTimeout(firstTimeout);
            if (intervalRef.current) clearInterval(intervalRef.current);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [intervalSec, waveDurationMs, charDurationMs, firstDelayMs, text]);

    return (
        <span className={`${styles.waveText} ${className ?? ''}`}>
            {letters.map((char, index) => (
                <span
                    key={index}
                    className={isAnimating ? styles.letter : undefined}
                    style={
                        isAnimating
                            ? {
                                animationDelay: `${(index * delayPerLetterMs) / 1000}s`,
                                animationDuration: `${charDurationMs / 1000}s`,
                            }
                            : undefined
                    }
                >
                    {char === ' ' ? '\u00A0' : char}
                </span>
            ))}
        </span>
    );
}
